/**
 * i18n setup. Wired from day one -- retrofitting costs several times what
 * building with it costs.
 *
 * Domain terms stay transliterated in BOTH locales ("Maasavari", "Zakat",
 * "Viduthal"): they are the community's own vocabulary, not English words to
 * be translated away.
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import enCommon from "@/locales/en/common.json";
import enCommunity from "@/locales/en/community.json";
import enDashboards from "@/locales/en/dashboards.json";
import enFinance from "@/locales/en/finance.json";
import enHousehold from "@/locales/en/household.json";
import enOperations from "@/locales/en/operations.json";
import enMadrasa from "@/locales/en/madrasa.json";
import enRegisters from "@/locales/en/registers.json";
import enSettings from "@/locales/en/settings.json";
import enSurvey from "@/locales/en/survey.json";
import mlCommon from "@/locales/ml/common.json";
import mlCommunity from "@/locales/ml/community.json";
import mlDashboards from "@/locales/ml/dashboards.json";
import mlFinance from "@/locales/ml/finance.json";
import mlHousehold from "@/locales/ml/household.json";
import mlOperations from "@/locales/ml/operations.json";
import mlMadrasa from "@/locales/ml/madrasa.json";
import mlRegisters from "@/locales/ml/registers.json";
import mlSettings from "@/locales/ml/settings.json";
import mlSurvey from "@/locales/ml/survey.json";

export const SUPPORTED_LOCALES = ["en", "ml"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        community: enCommunity,
        dashboards: enDashboards,
        registers: enRegisters,
        settings: enSettings,
        finance: enFinance,
        operations: enOperations,
        survey: enSurvey,
        madrasa: enMadrasa,
        household: enHousehold,
      },
      ml: {
        common: mlCommon,
        community: mlCommunity,
        dashboards: mlDashboards,
        registers: mlRegisters,
        settings: mlSettings,
        finance: mlFinance,
        operations: mlOperations,
        survey: mlSurvey,
        madrasa: mlMadrasa,
        household: mlHousehold,
      },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES,
    defaultNS: "common",
    ns: [
      "common",
      "community",
      "registers",
      "finance",
      "survey",
      "madrasa",
      "dashboards",
      "operations",
      "settings",
      "household",
    ],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "mh.locale",
      caches: ["localStorage"],
    },
  });

export function setLocale(locale: Locale): void {
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
}

export default i18n;
